import { useState, useMemo, useEffect } from "react";
import ActivityBookingButton from "./ActivityBookingButton";
import KlookBookingCard from "./KlookBookingCard";
import { getActivityImages } from "../../services/imageService";
import * as Sentry from "@sentry/react";
import { MediaError } from "../../errors/sentryErrors";

const getCategoryBadgeClass = (category) => {
  switch (category) {
    case 'sightseeing': return 'bg-blue-100 text-blue-700';
    case 'dining': return 'bg-orange-100 text-orange-700';
    case 'transport': return 'bg-purple-100 text-purple-700';
    case 'accommodation': return 'bg-green-100 text-green-700';
    case 'adventure': return 'bg-red-100 text-red-700';
    case 'shopping': return 'bg-pink-100 text-pink-700';
    case 'leisure': return 'bg-cyan-100 text-cyan-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'sightseeing': return '🏛️';
    case 'dining': return '🍽️';
    case 'transport': return '✈️';
    case 'accommodation': return '🏨';
    case 'adventure': return '🏔️';
    case 'shopping': return '🛍️';
    case 'leisure': return '🌳';
    default: return '📍';
  }
};

const ActivityCard = ({ activity, actIdx, isLastActivity, isLastDay = false, bookingLinks, tripSummary, localServices, travelPreferences, onEdit, onDelete, isEditable = false, dayDate, dayCity = '', hasChanges = false, onRevert }) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const destination = tripSummary?.destination || '';
  const allCities = tripSummary?.citiesBreakdown?.length
    ? tripSummary.citiesBreakdown.map(c => c.city)
    : destination ? [destination] : [];

  const klookCities = dayCity
    ? [dayCity]
    : (allCities[0] ? [allCities[0]] : []);

  useEffect(() => {
    let isMounted = true;

    const fetchImages = async () => {
      setIsLoading(true);
      setLoadedImages({});

      const locationForImages = dayCity || destination;

      try {
        const fetchedImages = await getActivityImages(
          activity,
          locationForImages,
          5,
          actIdx,
          travelPreferences
        );

        if (isMounted) {
          if (fetchedImages && fetchedImages.length > 0) {
            setImages(fetchedImages);
          } else {
            setImages([]);
          }
          setIsLoading(false);
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "media");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
        });
        if (isMounted) {
          setImages([]);
          setIsLoading(false);
        }
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
    };
  }, [activity.title, activity.category, destination, dayCity, actIdx]);

  const handleImageLoad = (idx) => {
    setLoadedImages(prev => ({ ...prev, [idx]: true }));
  };

  const handleImageError = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    if (selectedImageIdx >= images.length - 1) {
      setSelectedImageIdx(0);
    }
  };

  return (
    <div className="flex gap-2 sm:gap-4">
      <div className="flex flex-col items-center flex-shrink-0 pt-3">
        <div className="w-3 h-3 rounded-full bg-teal-500 flex-shrink-0"></div>
        {!isLastActivity && (
          <div className="w-0.5 flex-1 min-h-[40px] bg-teal-200 mt-1"></div>
        )}
      </div>

      <div className="flex-1 pb-4 min-w-0">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-[50%] flex-shrink-0">
              <div className="relative h-48 lg:h-56 overflow-hidden bg-gray-100">
                {isLoading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                    <div className="text-center">
                      <span className="text-4xl">{getCategoryIcon(activity.category)}</span>
                      <p className="text-xs text-gray-400 mt-2">Loading images...</p>
                    </div>
                  </div>
                ) : images.length > 0 ? (
                  <>
                    {!loadedImages[selectedImageIdx] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                        <span className="text-4xl">{getCategoryIcon(activity.category)}</span>
                      </div>
                    )}
                    <img
                      src={images[selectedImageIdx]?.url}
                      alt={images[selectedImageIdx]?.alt || activity.title}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${loadedImages[selectedImageIdx] ? 'opacity-100' : 'opacity-0'}`}
                      onLoad={() => handleImageLoad(selectedImageIdx)}
                      onError={() => handleImageError(selectedImageIdx)}
                    />
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                      {selectedImageIdx + 1} / {images.length}
                    </div>
                    {images[selectedImageIdx]?.photographer && (
                      <div className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] px-2 py-0.5 rounded">
                        📷 {images[selectedImageIdx].photographer}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                    <div className="text-center">
                      <span className="text-5xl">{getCategoryIcon(activity.category)}</span>
                      <p className="text-sm text-gray-500 mt-2 font-medium">{activity.title}</p>
                    </div>
                  </div>
                )}

                {images.length > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 lg:hidden">
                    <h4 className="font-semibold text-white text-sm line-clamp-1">{activity.title}</h4>
                  </div>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-1 p-1 bg-gray-50">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImageIdx(idx)}
                      className={`flex-1 h-12 overflow-hidden rounded transition-all cursor-pointer bg-gray-200 ${
                        selectedImageIdx === idx
                          ? 'ring-2 ring-teal-500'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={img.thumbnail}
                        alt={`${activity.title} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.opacity = '0'; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1 p-3 sm:p-4 flex flex-col min-w-0">
              <div className="mb-2">
                <div className="flex items-start justify-between gap-2">
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded mb-1 capitalize ${getCategoryBadgeClass(activity.category)}`}>
                    {activity.category || 'Activity'}
                  </span>
                  {isEditable && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {hasChanges && onRevert && (
                        <button
                          onClick={() => onRevert()}
                          className="px-2 py-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium cursor-pointer"
                          title="Revert to original"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          <span>Revert</span>
                        </button>
                      )}
                      <button
                        onClick={() => onEdit && onEdit(activity, actIdx)}
                        className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                        title="Edit activity"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete && onDelete(actIdx)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete activity"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 hidden lg:block line-clamp-2">{activity.title}</h4>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-3">{activity.description}</p>

              {activity.duration && (
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {activity.duration}
                </div>
              )}

              {activity.tips && (
                <div className="mb-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-800">
                  <span className="font-semibold">Tip:</span> {activity.tips}
                </div>
              )}

              <div className="mt-auto pt-2 flex justify-end border-t border-gray-100">
                <ActivityBookingButton
                  activity={activity}
                  bookingLinks={bookingLinks}
                  tripSummary={tripSummary}
                  localServices={localServices}
                  travelPreferences={travelPreferences}
                  dayDate={dayDate}
                  dayCity={dayCity}
                  isLastDay={isLastDay}
                />
              </div>

              <KlookBookingCard
                activity={activity}
                cities={klookCities}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;
